// BwaDrawerContent — Drilldown fuer BWA-Trend-Tile.
//
// Spec: "Monatsdetail + JA-Vergleich" + Konzern-Aggregat (Mt + YTD).
// Re-uses existing BwaTrendTile fuer DrainX + G+K (volle Hoehe).

import { useQuery } from "@tanstack/react-query";

import {
  type BwaItem,
  type BwaResponse,
  fetchBwa,
  formatEUR,
} from "../../../lib/api";
import BwaTrendTile from "../../BwaTrendTile";

// Severity-Trio fuer YTD-Marge (Spec: pink <0%, amber <5%, cyan >=5%).
function ytdMargeAccent(margePct: number | null): string {
  if (margePct === null) return "border-slate-700/50 bg-slate-900/40";
  if (margePct < 0) return "border-pink-500/40 bg-pink-500/[0.06]";
  if (margePct < 5) return "border-amber-500/40 bg-amber-500/[0.06]";
  return "border-cyan-500/40 bg-cyan-500/[0.06]";
}

function aggregateLatest(items: BwaItem[]): {
  periodLabel: string | null;
  monatUmsatz: number;
  monatEbit: number;
  ytdUmsatz: number;
  ytdEbit: number;
  ytdVorl: number;
  ytdMarge: number | null;
} {
  // Pro Firma den juengsten Eintrag — Aggregator-RLS liefert nur
  // xholding+drainx+gundk; xholding hat keine BWA, faellt natuerlich raus.
  const latestPerFirma = new Map<string, BwaItem>();
  for (const it of items) {
    const prev = latestPerFirma.get(it.firma_id);
    if (
      !prev ||
      it.jahr > prev.jahr ||
      (it.jahr === prev.jahr && it.monat > prev.monat)
    ) {
      latestPerFirma.set(it.firma_id, it);
    }
  }
  const latest = Array.from(latestPerFirma.values());
  const num = (s: string | null): number => (s ? parseFloat(s) : 0);

  const monatUmsatz = latest.reduce((a, it) => a + num(it.umsatz_eur), 0);
  const monatEbit = latest.reduce((a, it) => a + num(it.ergebnis_eur), 0);
  const ytdUmsatz = latest.reduce((a, it) => a + num(it.umsatz_eur_ytd), 0);
  const ytdEbit = latest.reduce(
    (a, it) => a + num(it.betriebsergebnis_eur_ytd),
    0,
  );
  const ytdVorl = latest.reduce(
    (a, it) => a + num(it.vorlaeufiges_ergebnis_eur_ytd),
    0,
  );
  const ytdMarge = ytdUmsatz > 0 ? (ytdEbit / ytdUmsatz) * 100 : null;

  let periodLabel: string | null = null;
  if (latest.length > 0) {
    const maxJahr = Math.max(...latest.map((it) => it.jahr));
    const maxMonat = Math.max(
      ...latest.filter((it) => it.jahr === maxJahr).map((it) => it.monat),
    );
    periodLabel = `${String(maxMonat).padStart(2, "0")}/${maxJahr}`;
  }
  return {
    periodLabel,
    monatUmsatz,
    monatEbit,
    ytdUmsatz,
    ytdEbit,
    ytdVorl,
    ytdMarge,
  };
}

interface KpiPairProps {
  label: string;
  monatValue: string;
  ytdValue: string;
  accent?: string;
}

function KpiPair({ label, monatValue, ytdValue, accent }: KpiPairProps) {
  const accentCls = accent ?? "border-slate-700/50 bg-slate-900/40";
  // C1 (#54 Bug 3, 2026-05-21): grid-cols-2 nebeneinander hat bei grossen
  // Konzern-Summen (Sechs-Stellig+) zu Overlap "168.884.517.095,27€" gefuehrt.
  // Vertikales 2-Zeilen-Stacking ist robust auch bei breiten Werten.
  return (
    <div className={`rounded-xl border p-3 ${accentCls}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-60">{label}</div>
      <div className="mt-2 flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between gap-2 min-w-0">
          <div className="text-[9px] uppercase tracking-widest opacity-50 shrink-0">
            lfd Mt
          </div>
          <div className="text-base font-semibold tabular-nums truncate">{monatValue}</div>
        </div>
        <div className="flex items-baseline justify-between gap-2 min-w-0">
          <div className="text-[9px] uppercase tracking-widest opacity-50 shrink-0">YTD</div>
          <div className="text-base font-semibold tabular-nums truncate">{ytdValue}</div>
        </div>
      </div>
    </div>
  );
}

function KonzernAggregatSection() {
  const q = useQuery<BwaResponse, Error>({
    queryKey: ["bwa", "konzern-aggregat"],
    queryFn: () => fetchBwa({ limit: 24 }),
    staleTime: 10 * 60_000,
  });
  if (q.isLoading || !q.data) return null;
  const agg = aggregateLatest(q.data.items);
  if (agg.periodLabel === null) return null;
  const ebitAccent = ytdMargeAccent(agg.ytdMarge);
  return (
    <section className="rounded-xl border border-slate-700/60 bg-slate-950/40 p-3">
      <div className="flex items-baseline justify-between mb-2 gap-2 flex-wrap">
        <div className="text-[11px] uppercase tracking-widest opacity-60">
          Konzern-Aggregat (DrainX + G+K)
        </div>
        <div className="text-[10px] text-slate-400 tabular-nums">
          Stand BWA {agg.periodLabel}
          {agg.ytdMarge !== null && ` · YTD-Marge ${agg.ytdMarge.toFixed(1)}%`}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <KpiPair
          label="Konzern-Umsatz"
          monatValue={formatEUR(agg.monatUmsatz.toString())}
          ytdValue={formatEUR(agg.ytdUmsatz.toString())}
        />
        <KpiPair
          label="Konzern-EBIT (Betriebsergebnis)"
          monatValue={formatEUR(agg.monatEbit.toString())}
          ytdValue={formatEUR(agg.ytdEbit.toString())}
          accent={ebitAccent}
        />
        <KpiPair
          label="Konzern-Ergebnis (vorläufig)"
          monatValue="—"
          ytdValue={formatEUR(agg.ytdVorl.toString())}
        />
      </div>
    </section>
  );
}

export default function BwaDrawerContent() {
  return (
    <>
      <section className="rounded-xl border border-slate-700/60 bg-slate-950/40 p-3">
        <p className="text-[11px] text-slate-400">
          BWA-Pipeline: Pahl-Diederichsen-Mail → mail-briefing-daemon (06:30) →
          STB-Klassifikator → POST /api/v1/ingest/stb-pdf → bwa-Tabelle.
          Aufwand-Quote = Aufwendungen / Umsatz. JA-Anker = Vorjahres-Dezember-
          BWA als ReferenceLine. YTD = Jan-bis-aktueller-Monat (rechte Pahl-
          Spalte, ehrlichere Sicht weil monatliche Schwankungen geglaettet).
        </p>
      </section>

      <KonzernAggregatSection />

      <BwaTrendTile firmaId="drainx" />
      <BwaTrendTile firmaId="gundk" />

      <section className="rounded-xl border border-slate-700/60 bg-slate-950/40 p-3">
        <div className="text-[11px] uppercase tracking-widest opacity-60 mb-2">
          Hinweise
        </div>
        <ul className="text-[11px] text-slate-300 space-y-1.5 list-disc pl-4">
          <li>xholding hat keine BWA (Holding macht nur Jahresabschluss).</li>
          <li>InfraX out-of-scope (R1, eigener Datenraum).</li>
          <li>
            Personalkosten-Approximation aus Aufwand — Backend-Schema-Erweiterung
            personalkosten_eur folgt mit DATEV-Schluessel-Mapping.
          </li>
        </ul>
      </section>
    </>
  );
}
